import React, { useState, useEffect, createContext, useContext } from 'react';

interface RouterContextType {
  path: string;
  push: (path: string) => void;
}

const RouterContext = createContext<RouterContextType>({
  path: '/',
  push: () => {}
});

export const useRouter = () => useContext(RouterContext);

export const RouterProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // Use the browser history API (no hashes)
  const getPath = () => {
    const pathname = window.location.pathname || '/';
    const search = window.location.search || '';
    return pathname + search;
  };

  const [path, setPath] = useState(getPath());

  useEffect(() => {
    const onPopState = () => {
      setPath(getPath());
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const push = (newPath: string) => {
    if (newPath === path) return;
    window.history.pushState({}, '', newPath);
    setPath(getPath());
    window.scrollTo(0, 0);
  };

  return (
    <RouterContext.Provider value={{ path, push }}>
      {children}
    </RouterContext.Provider>
  );
};

interface LinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  primary?: boolean;
  target?: string;
  rel?: string;
}

export const Link: React.FC<LinkProps> = ({ to, children, className, target, rel }) => {
  const { push } = useRouter();
  return (
    <a
      href={to}
      className={className}
      target={target}
      rel={rel}
      onClick={(e) => {
        // allow modifier + middle-click to open in new tab/window
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        push(to);
      }}
    >
      {children}
    </a>
  );
};